import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cpu, Camera, Plus, Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { getMyDevices, resetDevice } from '../../api/devices';
import { useSuccessToast } from '../../store/successToastStore';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
interface Device {
  deviceUid: string;
  deviceType?: string;
  status: 'active' | 'inactive';
  activatedAt: string;
  lastSeenAt: string;
}
interface DeviceManagementProps {
  isPanel?: boolean;
}
export function DeviceManagement({
  isPanel = false
}: DeviceManagementProps) {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const {
    showToast
  } = useSuccessToast();
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [deviceToRemove, setDeviceToRemove] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showSetupPicker, setShowSetupPicker] = useState(false);
  const startSetup = (type: 'belt' | 'cradle') => {
    setShowSetupPicker(false);
    navigate(`/app/setup?type=${type}`);
  };
  const isCradleDevice = (d: Device) => !!d.deviceType && d.deviceType.toLowerCase().includes('cradle');
  const fetchDevices = async () => {
    if (!token) return;
    setIsLoading(true);
    setError(false);
    try {
      const res = await getMyDevices(token);
      setDevices(res || []);
    } catch (err) {
      // console.error('Failed to fetch devices', err)
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchDevices();
  }, [token]);
  const handleRemove = async () => {
    if (!token || !deviceToRemove) return;
    setIsRemoving(true);
    try {
      await resetDevice(token, deviceToRemove);
      setDevices(prev => prev.filter(d => d.deviceUid !== deviceToRemove));
      showToast('Device removed');
      setDeviceToRemove(null);
    } catch (err) {
      // console.error('Failed to remove device', err)
    } finally {
      setIsRemoving(false);
    }
  };
  const getRelativeTime = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hr ago`;
    const isYesterday = new Date(now.setDate(now.getDate() - 1)).toDateString() === date.toDateString();
    if (isYesterday) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      })}`;
    }
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric'
    });
  };
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return `Connected ${date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    })}`;
  };
  return <main className="w-full max-w-[600px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-1826" data-mp="3bjicv1sodsmk">
      {/* Header */}
      {!isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1827" data-mp="3lbo3o876y406">
          <div className="flex items-center" data-id="element-1828" data-mp="3ld8y7xti9my1">
            <button onClick={() => navigate('/app/profile')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-1829" data-mp="35ryg5cru0ql">
              <ArrowLeft size={24} className="text-text-primary" data-id="element-1830" data-mp="24ypuea1uh9fr" />
            </button>
            <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1831" data-mp="1zzwg4feqheej">
              Connected Devices
            </h1>
          </div>

          <button onClick={() => setShowSetupPicker(true)} className="w-[36px] h-[36px] rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 transition-colors" aria-label="Add Device" data-id="element-1832" data-mp="26dit1gbhp3nh">
            <Plus size={20} className="text-white" data-id="element-1833" data-mp="2gbgczrmbgeaa" />
          </button>
        </div>}

      {isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1834" data-mp="1immrvsl58w3k">
          <h2 className="text-[24px] font-semibold text-text-primary" data-id="element-1835" data-mp="1no3sro3moik5">
            Connected Devices
          </h2>
          <button onClick={() => setShowSetupPicker(true)} className="px-4 py-2 rounded-pill bg-primary text-white text-[14px] font-semibold flex items-center gap-2 hover:bg-primary/90 transition-colors" data-id="element-1836" data-mp="1slcd9vc90yok">
            <Plus size={16} data-id="element-1837" data-mp="14wt1v6xwk5s9" />
            Add Device
          </button>
        </div>}

      {/* Content */}
      {isLoading ? <div className="space-y-4" data-id="element-1838" data-mp="xc8u5tepzrjh">
          {[1, 2].map(i => <SkeletonBlock key={i} height="160px" borderRadius="20px" data-id="element-1839" data-mp="sdxeh21qumtc" />)}
        </div> : error ? <div className="bg-danger/10 border border-danger/20 rounded-[16px] p-6 text-center" data-id="element-1840" data-mp="neckl61fl4nh">
          <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-1841" data-mp="if9cfcq5miba" />
          <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-1842" data-mp="eefqgow9vdl1">
            Failed to load devices
          </h3>
          <p className="text-[14px] text-text-secondary mb-4" data-id="element-1843" data-mp="9mxxan0hqham">
            There was a problem connecting to the server.
          </p>
          <Button onClick={fetchDevices} className="mx-auto" data-id="element-1844" data-mp="4ms2ixyxceob">
            Try Again
          </Button>
        </div> : devices.length === 0 ? <div className="bg-white rounded-[24px] p-10 shadow-card text-center flex flex-col items-center" data-id="element-1845" data-mp="3qwch9p1jdnls">
          <div className="w-[120px] h-[120px] mb-6 relative" data-id="element-1846" data-mp="3mvk80vsnzipi">
            <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1847" data-mp="3c9dui7dmjhde">
              <rect x="10" y="40" width="100" height="40" rx="20" stroke="#E9D5FF" strokeWidth="4" strokeDasharray="8 8" data-id="element-1848" data-mp="3h9jse5ahm03x" />
              <circle cx="60" cy="60" r="12" fill="#E9D5FF" data-id="element-1849" data-mp="3vxmxzdkmb9s7" />
            </svg>
          </div>
          <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1850" data-mp="3tkl0chpn65u">
            No devices connected
          </h3>
          <p className="text-[15px] text-text-secondary mb-8 max-w-[280px]" data-id="element-1851" data-mp="3m8d2rouh9azv">
            Set up your Aurora Care belt or cradle to start monitoring.
          </p>
          <div className="w-full max-w-[280px] space-y-3" data-id="element-1852" data-mp="3r4bpl805qky6">
            <Button onClick={() => startSetup('belt')} data-id="element-1853" data-mp="iq338ho8aqeg">Set Up Belt</Button>
            <Button onClick={() => startSetup('cradle')} variant="secondary" data-id="element-1854" data-mp="nhtecpuzpi6x">
              Set Up Cradle
            </Button>
          </div>
        </div> : <div className="space-y-4" data-id="element-1855" data-mp="suru0xe9t2qt">
          <AnimatePresence data-id="element-1856" data-mp="stqheohlw86z">
            {devices.map(device => {
          const isCradle = isCradleDevice(device);
          return <motion.div key={device.deviceUid} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95,
            height: 0,
            marginBottom: 0
          }} transition={{
            duration: 0.3
          }} className="bg-white rounded-[20px] shadow-card p-5 overflow-hidden" data-id="element-1857" data-mp="16o0z4lv6r23j">
                  <div className="flex items-start gap-4 mb-4" data-id="element-1858" data-mp="1udv9mws18ekm">
                    <div className={`w-[56px] h-[56px] rounded-full flex items-center justify-center flex-shrink-0 ${isCradle ? 'bg-accent' : 'bg-primary'}`} data-id="element-1859" data-mp="3otagwuqxoa5r">
                      {isCradle ? <Camera size={24} className="text-white" data-id="element-1860" data-mp="1lghx3qhltoz" /> : <Cpu size={24} className="text-white" data-id="element-1861" data-mp="6fuj5mrtyv1k" />}
                    </div>

                    <div className="flex-1 min-w-0 pt-1" data-id="element-1862" data-mp="gc5vudvc1mwj">
                      <h3 className="text-[15px] font-semibold text-text-primary font-mono tracking-wider mb-0.5" data-id="element-1863" data-mp="gamhwtx5fnxx">
                        {device.deviceUid}
                      </h3>
                      <p className="text-[13px] text-text-secondary mb-1" data-id="element-1864" data-mp="pbkmpefevzhk">
                        {device.deviceType || (isCradle ? 'Aurora Care Cradle' : 'Aurora Care Belt')}
                      </p>
                      <p className="text-[12px] text-text-secondary" data-id="element-1865" data-mp="2jkzhbwrdis9c">
                        {formatDate(device.activatedAt)}
                      </p>
                    </div>

                    <div className="flex-shrink-0 pt-1" data-id="element-1866" data-mp="29umzicay1d6w">
                      {device.status === 'active' ? <div className="flex items-center gap-2" data-id="element-1867" data-mp="24wl6oznptzi8">
                          <div className="relative w-2.5 h-2.5" data-id="element-1868" data-mp="24tz31b3y445a">
                            <div className="absolute inset-0 bg-success rounded-full animate-ping opacity-75" data-id="element-1869" data-mp="32j24s749wh51" />
                            <div className="relative w-full h-full bg-success rounded-full" data-id="element-1870" data-mp="2xlaefhja132y" />
                          </div>
                          <span className="text-[13px] font-medium text-success" data-id="element-1871" data-mp="2ojsxjwy1oxd7">
                            Active
                          </span>
                        </div> : <div className="flex items-center gap-2" data-id="element-1872" data-mp="1d4vw1nkocfoy">
                          <div className="w-2.5 h-2.5 bg-[#8A8A8A] rounded-full" data-id="element-1873" data-mp="1d6qij6blgfv5" />
                          <span className="text-[13px] font-medium text-text-secondary" data-id="element-1874" data-mp="a0ww64hvx0li">
                            Offline
                          </span>
                        </div>}
                    </div>
                  </div>

                  <div className="h-[1px] w-full bg-border mb-4" data-id="element-1875" data-mp="eyyf8pfb8zwz" />

                  <div className="flex items-center justify-between" data-id="element-1876" data-mp="exnbgplv7s5v">
                    <div className="flex items-center gap-1.5 text-text-secondary" data-id="element-1877" data-mp="tmtaodkpdidr">
                      <Clock size={14} data-id="element-1878" data-mp="oohz9yereztf" />
                      <span className="text-[13px]" data-id="element-1879" data-mp="ormahfqn6vtl">
                        Last seen {getRelativeTime(device.lastSeenAt)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2" data-id="element-1880" data-mp="xqr88judql4p">
                      <button onClick={() => setDeviceToRemove(device.deviceUid)} className="border border-danger text-danger rounded-pill px-[14px] py-[6px] text-[13px] font-medium hover:bg-danger/5 transition-colors" data-id="element-1881" data-mp="34fmfggfv09zs">
                        Remove
                      </button>
                    </div>
                  </div>
                </motion.div>;
        })}
          </AnimatePresence>
        </div>}

      {/* Remove Confirmation Bottom Sheet */}
      <AnimatePresence data-id="element-1882" data-mp="1a67frjp1ir15">
        {deviceToRemove && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => !isRemoving && setDeviceToRemove(null)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1883" data-mp="w77rwe9cqg61" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1884" data-mp="2hlvmckvmx4s9">
              <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6 md:hidden" data-id="element-1885" data-mp="h6aoarsl4mpo" />
              <div className="text-center" data-id="element-1886" data-mp="d4fltjyx43ab">
                <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-1887" data-mp="d4pexgf9eqk8">
                  Remove this device?
                </h2>
                <p className="text-[15px] text-text-secondary mb-6" data-id="element-1888" data-mp="3edzawm7qsxi">
                  This will disconnect this device from your account. You will
                  need to set it up again to use it.
                </p>

                <div className="bg-secondary inline-block px-4 py-2 rounded-pill font-mono text-[14px] font-semibold text-text-primary mb-8" data-id="element-1889" data-mp="w2ib7zvdo28n">
                  {deviceToRemove}
                </div>

                <button onClick={handleRemove} disabled={isRemoving} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] flex items-center justify-center transition-opacity disabled:opacity-70 mb-3" data-id="element-1890" data-mp="18chm2v96vjn4">
                  {isRemoving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1891" data-mp="1mcsfo61vvcbz" /> : 'Yes, Remove'}
                </button>
                <button onClick={() => setDeviceToRemove(null)} disabled={isRemoving} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-1892" data-mp="1w4ye9ymn4s05">
                  Cancel
                </button>
              </div>
            </motion.div>
          </>}

        {/* Device Setup Picker Bottom Sheet */}
        {showSetupPicker && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowSetupPicker(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1893" data-mp="1sdt1b1u1xozx" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)] md:max-w-[480px] md:mx-auto md:rounded-[24px] md:bottom-6 md:left-6 md:right-6" data-id="element-1894" data-mp="19fyixx71a14v">
              <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-5" data-id="element-1895" data-mp="kmm03l6ctx7b" />
              <h2 className="text-[20px] font-semibold text-text-primary text-center mb-1" data-id="element-1896" data-mp="kk98shepiahl">
                Add a device
              </h2>
              <p className="text-[14px] text-text-secondary text-center mb-6" data-id="element-1897" data-mp="bl5pbcbmnt2k">
                Which Aurora Care device are you setting up?
              </p>

              <div className="space-y-3" data-id="element-1898" data-mp="zdvzbfzz02a7">
                <button onClick={() => startSetup('belt')} className="w-full flex items-center gap-4 p-4 bg-white border border-border rounded-[16px] hover:border-primary hover:bg-primary/5 transition-colors text-left" data-id="element-1899" data-mp="13dd7ih6s7dj2">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0" data-id="element-1900" data-mp="1ianq0ahudjoa">
                    <Cpu size={22} className="text-white" data-id="element-1901" data-mp="1n1cfjk9yo6ks" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1902" data-mp="1wwt3ajfeumau">
                    <h3 className="text-[16px] font-semibold text-text-primary" data-id="element-1903" data-mp="1wwkt4zao9v3c">
                      Aurora Care Belt
                    </h3>
                    <p className="text-[13px] text-text-secondary" data-id="element-1904" data-mp="1myn5acwq60b9">
                      Fetal heart rate &amp; contractions
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-text-secondary flex-shrink-0" data-id="element-1905" data-mp="1i1wrxxcb1itj" />
                </button>

                <button onClick={() => startSetup('cradle')} className="w-full flex items-center gap-4 p-4 bg-white border border-border rounded-[16px] hover:border-accent hover:bg-accent/5 transition-colors text-left" data-id="element-1906" data-mp="195xhq41jsnc1">
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center flex-shrink-0" data-id="element-1907" data-mp="uahkxr9l2dby">
                    <Camera size={22} className="text-white" data-id="element-1908" data-mp="q9xkoetpqtll" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1909" data-mp="31uy54javcoxl">
                    <h3 className="text-[16px] font-semibold text-text-primary" data-id="element-1910" data-mp="31yc55kw28m1v">
                      Aurora Care Cradle
                    </h3>
                    <p className="text-[13px] text-text-secondary" data-id="element-1911" data-mp="3axpkgu6v84ui">
                      Live baby camera
                    </p>
                  </div>
                  <ChevronRight size={20} className="text-text-secondary flex-shrink-0" data-id="element-1912" data-mp="3ftej8vb29hpr" />
                </button>
              </div>

              <button onClick={() => setShowSetupPicker(false)} className="w-full mt-5 h-[48px] text-text-secondary rounded-[14px] font-medium text-[15px]" data-id="element-1913" data-mp="2n4qugpr3sa7g">
                Cancel
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}